import { classifyStatus, PlaybackError } from './errors';
import { fetchWithCredential, joinUrl } from './http';
import { type CredentialSource, type MatchLiveStatus, type MatchStatus } from './types';

export interface WatchStatusOptions {
  baseUrl: string;
  matchUrn: string;
  credential: CredentialSource;
  /** Fired whenever the status changes (and once on first read). */
  onChange: (status: MatchStatus) => void;
  /** Fired on a poll error; polling continues. */
  onError?: (err: PlaybackError) => void;
  /** Poll interval in ms. Floored at 2000. Default: 10000 (catalog max-age). */
  intervalMs?: number;
  /**
   * Extra headers attached to every request to {@link baseUrl} — and only
   * there: never to CDN or license hosts. `x-api-key` cannot be overridden.
   */
  apiHeaders?: Record<string, string>;
}

export interface StatusWatcher {
  stop(): void;
}

/**
 * Poll `GET /v1/matches/{urn}` for the live status (there is no push channel),
 * firing onChange on transitions. Uses ETag/If-None-Match so unchanged polls
 * are cheap 304s. This drives discovery; actual readiness is the /v1/playback
 * 200 (see resolveStream + waitForLive).
 */
export function watchStatus(opts: WatchStatusOptions): StatusWatcher {
  const interval = Math.max(2000, opts.intervalMs ?? 10_000);
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let etag: string | undefined;
  let lastStatus: MatchLiveStatus | undefined;
  let stopped = false;

  const schedule = () => {
    if (!stopped) timer = setTimeout(tick, interval);
  };

  const tick = async () => {
    try {
      const url = joinUrl(opts.baseUrl, `/v1/matches/${encodeURIComponent(opts.matchUrn)}`);
      const headers = new Headers();
      if (etag) headers.set('If-None-Match', etag);
      const res = await fetchWithCredential(url, {
        credential: opts.credential,
        signal: controller.signal,
        headers,
        apiHeaders: opts.apiHeaders,
      });
      if (res.status !== 304) {
        if (!res.ok) {
          throw new PlaybackError(
            classifyStatus(res.status),
            res.status,
            `match status request failed (HTTP ${res.status})`,
          );
        }
        etag = res.headers.get('etag') ?? etag;
        const m = (await res.json()) as {
          matchUrn?: string;
          matchName?: string;
          status?: string;
          datePlannedStart?: string;
        };
        const status = (m.status as MatchLiveStatus) ?? 'upcoming';
        if (status !== lastStatus) {
          lastStatus = status;
          opts.onChange({
            matchUrn: m.matchUrn ?? opts.matchUrn,
            matchName: m.matchName,
            status,
            datePlannedStart: m.datePlannedStart,
          });
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        opts.onError?.(
          err instanceof PlaybackError ? err : new PlaybackError('NETWORK', 0, String(err)),
        );
      }
    } finally {
      schedule();
    }
  };

  void tick();

  return {
    stop() {
      stopped = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    },
  };
}
